import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import {
  audioPlaybackRates,
  playbackRateAsString
} from "../../../helpers/playbackRates";
import i18n from "../../../service/i18n";
import { usePlayerStore } from "../../../store/usePlayerStore";
import { focusColor } from "../../../styles/variables";
import { ThemeColors } from "../getThemeColors";

const StyledPlaybackRateToggle = styled.button<{
  isMobile: boolean;
  themeColors: ThemeColors;
}>`
  background: transparent;
  border-radius: 4px;
  border: none;
  height: ${props => (props.isMobile ? "40px" : "auto")};
  margin: 0 4px;
  padding: 2px;

  div {
    border-radius: 4px;
    border: 2px solid ${props => props.themeColors.fillColor};
    color: ${props => props.themeColors.fillColor};
    font-size: 12px;
    font-weight: 700;
    padding: 3px 6px;
  }

  &:focus-visible {
    outline: 2px solid ${focusColor} !important;
  }

  &:hover {
    border-color: ${props => props.themeColors.hoverColor};
    color: ${props => props.themeColors.hoverColor};
    cursor: pointer;
  }
`;

interface Props {
  language: string;
  themeColors: ThemeColors;
}

export const PlaybackRateToggle = ({ language, themeColors }: Props) => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const playbackRate = useStoreState.playbackRate();
  const player = useStoreState.player();

  const [displayText, setDisplayText] = useState<string>("");

  useEffect(() => {
    setDisplayText(playbackRateAsString(playbackRate, language));
  }, [playbackRate, language]);

  const onClickPlaybackRateToggle = useCallback(() => {
    const closestStep = audioPlaybackRates.reduce(
      (previousValue, currentValue) =>
        currentValue <= playbackRate ? currentValue : previousValue,
      0
    );

    const currentIndex = audioPlaybackRates.indexOf(closestStep);

    player.setPlaybackRate(
      currentIndex === -1 || currentIndex >= audioPlaybackRates.length - 1
        ? audioPlaybackRates[0]
        : audioPlaybackRates[currentIndex + 1]
    );
  }, [playbackRate, player]);

  return (
    <StyledPlaybackRateToggle
      aria-label={`${i18n("toistonopeus", { language })} ${displayText}`}
      isMobile={device.isMobile}
      onClick={onClickPlaybackRateToggle}
      themeColors={themeColors}
      type="button">
      <div>{displayText}X</div>
    </StyledPlaybackRateToggle>
  );
};
